import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, errDetail, fmtDate, fmtUSD } from "@/lib/api";

interface BalanceInfo {
  id: string; yacht_id: string; yacht_name: string; date: string; block: string; guests: number; name: string; status: string;
  total: number; deposit: number; balance_paid: number; remaining: number; next_charge: number; settled: boolean;
  payments: { amount: number; at: string }[];
}

function Row({ k, v, id, strong }: { k: string; v: React.ReactNode; id?: string; strong?: boolean }) {
  return <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 text-sm"><span className="text-slate-500">{k}</span><span data-testid={id} className={strong ? "font-semibold text-[#0B192C]" : "text-[#0B192C]"}>{v}</span></div>;
}

export default function Balance() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const returned = params.get("return") === "1";
  const { t, lang } = useLang();
  const [paying, setPaying] = useState(false);
  const [waited, setWaited] = useState(0);
  const confirming = returned && waited < 24;
  const { data: b, isLoading, error, refetch } = useQuery<BalanceInfo>({
    queryKey: ["balance", id, token], retry: false,
    queryFn: async () => (await api.get(`/balance/${id}`, { params: { t: token } })).data,
    refetchInterval: (q) => (confirming && q.state.data && !q.state.data.settled ? 2500 : false),
  });
  useEffect(() => {
    if (!confirming || !b || b.settled) return;
    const tm = setTimeout(() => setWaited((w) => w + 1), 2500);
    return () => clearTimeout(tm);
  }, [confirming, b, waited]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/balance/${id}/checkout`, null, { params: { t: token } });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(errDetail(e) === "payment_provider_error" ? t("co.providerError") : t("err.generic"));
      setPaying(false);
    }
  };

  return (
    <div className="bg-[#F6F3EC] min-h-[70vh]" data-testid="balance-page">
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="eyebrow">{t("bal.eyebrow")}</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl text-[#0B192C]">{t("bal.title")}</h1>
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
          {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-[#00A8A0]" /> : error || !b ? (
            <p data-testid="balance-invalid" className="text-sm text-slate-600">{t("bal.invalid")}</p>
          ) : (
            <>
              <p className="text-sm text-slate-600">{t("bal.hello", { name: b.name })}</p>
              <div className="mt-5">
                <Row k={t("co.ref")} v={b.id.slice(0, 8).toUpperCase()} />
                <Row k={t("nav.yachts")} v={b.yacht_name} />
                <Row k={t("search.date")} v={fmtDate(b.date, lang)} />
                <Row k={t("book.block").replace(/^\d\.\s/, "")} v={t(`block.${b.block}`)} />
                <Row k={t("book.total")} v={`${fmtUSD(b.total)} USD`} id="balance-total" />
                <Row k={t("co.depositPaid")} v={`${fmtUSD(b.deposit)} USD`} id="balance-deposit" />
                {b.balance_paid > 0 && <Row k={t("bal.paidOnline")} v={`${fmtUSD(b.balance_paid)} USD`} id="balance-paid" />}
                <Row k={t("bal.remaining")} v={`${fmtUSD(b.remaining)} USD`} id="balance-remaining" strong />
              </div>
              {b.settled ? (
                <div className="mt-8 text-center" data-testid="balance-settled">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-[#00A8A0]" />
                  <h2 className="mt-3 font-display text-2xl text-[#0B192C]">{t("bal.settledTitle")}</h2>
                  <p className="mt-2 text-sm text-slate-600">{t("bal.settledBody")}</p>
                  <Link to="/" className="mt-6 inline-flex rounded-full bg-[#0B192C] px-6 py-3 text-sm font-semibold text-white">{t("co.home")}</Link>
                </div>
              ) : (
                <>
                  {returned && (
                    <p data-testid="balance-confirming-note" className="mt-5 flex items-start gap-2 rounded-md bg-sky-50 border border-sky-200 px-4 py-3 text-xs text-sky-800">
                      {confirming && <Loader2 className="h-4 w-4 shrink-0 animate-spin" />}{confirming ? t("co.confirming") : t("co.confirmSlow")}
                    </p>
                  )}
                  {b.next_charge < b.remaining - 0.01 && <p data-testid="balance-split-note" className="mt-5 rounded-md bg-[#F3E5AB]/40 border border-[#D4AF37]/40 px-4 py-3 text-xs text-[#0B192C]">{t("bal.splitNote", { max: fmtUSD(b.next_charge) })}</p>}
                  <button onClick={pay} disabled={paying} data-testid="balance-pay-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-4 text-sm font-semibold text-[#0B192C] hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">
                    {paying && <Loader2 className="h-4 w-4 animate-spin" />}{paying ? t("co.paying") : t("bal.pay", { amount: fmtUSD(b.next_charge) })}
                  </button>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5" />{t("co.liveNote")}</p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
